import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { services, portfolio, testimonials, whyUs, processSteps } from '../data/siteData';
import { useReveal } from '../hooks/useReveal';
import './Home.css';

/* ── animated counter ── */
function Counter({ target, suffix = '', decimal = false }) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const dur = 1400; let start;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setVal(decimal ? (ease * target / 10).toFixed(1) : Math.floor(ease * target));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, decimal]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function RevealDiv({ className, children, style }) {
  const ref = useReveal();
  return <div ref={ref} className={`reveal ${className || ''}`} style={style}>{children}</div>;
}

export default function Home() {
  return (
    <div className="home">
      {/* ── HERO ── */}
      <section className="hero">
        <div className="orb orb-1" /><div className="orb orb-2" /><div className="orb orb-3" />
        <div className="container hero-inner">
          <div className="badge" style={{ animationDelay: '0s' }}>
            <div className="badge-dot">✦</div>
            Trusted by 200+ companies worldwide
          </div>
          <h1>
            Where Digital Meets<br />
            <span className="grad-text">Intelligence</span>
          </h1>
          <p className="hero-sub">
            We build web apps, mobile products, AI integrations, and growth systems that compound — so your ROI doesn't plateau.
          </p>
          <div className="hero-actions">
            <Link to="/get-quote" className="btn-ink">Start your project →</Link>
            <Link to="/portfolio" className="btn-out">See our work</Link>
          </div>

          {/* Feature chips */}
          <div className="hero-strip">
            {services.map(s => (
              <Link to={`/services/${s.id}`} key={s.id} className="strip-chip">
                <div className="strip-icon" style={{ background: s.color }}>{s.icon}</div>
                {s.title}
              </Link>
            ))}
          </div>

          {/* Stats */}
          <div className="hero-stats">
            <div className="hstat">
              <div className="hstat-n grad-text"><Counter target={200} suffix="+" /></div>
              <div className="hstat-l">Projects delivered</div>
            </div>
            <div className="hstat">
              <div className="hstat-n teal-text"><Counter target={98} suffix="%" /></div>
              <div className="hstat-l">Client satisfaction</div>
            </div>
            <div className="hstat">
              <div className="hstat-n grad-text"><Counter target={32} suffix="×" decimal /></div>
              <div className="hstat-l">Average ROI</div>
            </div>
            <div className="hstat">
              <div className="hstat-n" style={{ color: '#2563EB', fontWeight: 900, fontSize: 30, letterSpacing: '-0.04em' }}>7 yrs</div>
              <div className="hstat-l">Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST LOGOS ── */}
      <section className="trust">
        <div className="container">
          <p className="trust-label">Trusted by forward-thinking teams</p>
          <div className="trust-logos">
            {['VERITAS','ORBIFY','STACKLANE','CRESTDATA','NOVAPEAK','SYNQTECH'].map(l => (
              <span key={l} className="trust-logo">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="section" id="services">
        <div className="container">
          <RevealDiv className="section-center" style={{ marginBottom: 48 }}>
            <div className="s-tag">Services</div>
            <h2 className="s-head">Everything your product needs</h2>
            <p className="s-sub">One team across every layer of the stack — from first pixel to production at scale.</p>
          </RevealDiv>
          <div className="bento">
            {services.map(s => (
              <RevealDiv key={s.id}>
                <article className="bcard">
                  <div className="bcard-glow" style={{ background: `radial-gradient(circle,${s.glowColor},transparent)` }} />
                  <div className="bcard-icon" style={{ background: s.color }}>{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.shortDesc}</p>
                  <Link to={`/services/${s.id}`} className="bcard-link">Learn more →</Link>
                </article>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="section why-section">
        <div className="container">
          <div className="why-wrap">
            <RevealDiv>
              <div className="s-tag" style={{ color: '#7aa5ff' }}>Why NextGenHub</div>
              <h2 className="s-head" style={{ color: '#fff' }}>Built for outcomes,<br />not outputs</h2>
              <div className="why-list">
                {whyUs.map(w => (
                  <div key={w.title} className="why-item">
                    <div className="why-dot" style={{ background: w.bg }}>{w.icon}</div>
                    <div className="why-text">
                      <h4>{w.title}</h4>
                      <p>{w.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </RevealDiv>
            <RevealDiv>
              <div className="why-visual">
                <div className="why-visual-blob" />
                <p className="why-visual-label">Performance at a glance</p>
                <div className="metric-cards">
                  {[
                    { num: '214%', label: 'Avg. traffic lift',    color: '#9747FF', fill: 74 },
                    { num: '38%',  label: 'Conv. rate gain',      color: '#0AC5A8', fill: 38 },
                    { num: '70%',  label: 'Support deflection',   color: '#FF6B35', fill: 70 },
                    { num: '3×',   label: 'Pipeline in 90 days',  color: '#2563EB', fill: 60 },
                  ].map(m => (
                    <div key={m.label} className="mc">
                      <div className="mc-num" style={{ color: m.color }}>{m.num}</div>
                      <div className="mc-label">{m.label}</div>
                      <div className="mc-bar"><div className="mc-fill" style={{ width: `${m.fill}%`, background: m.color }} /></div>
                    </div>
                  ))}
                </div>
                <div className="why-launch-card">
                  <div className="wlc-icon">⚡</div>
                  <div>
                    <div className="wlc-title">Average time-to-launch</div>
                    <div className="wlc-sub">Web apps in <b>6–10 weeks</b>, mobile in <b>10–14 weeks</b></div>
                  </div>
                </div>
              </div>
            </RevealDiv>
          </div>
        </div>
      </section>

      {/* ── MID CTA ── */}
      <section className="section">
        <div className="container">
          <RevealDiv>
            <div className="mid-cta-wrap">
              <h2>Ready to build something people love?</h2>
              <p>Book a free 30-minute strategy session. No sales pitch — just clarity on your next move.</p>
              <div className="mid-cta-btns">
                <Link to="/get-quote" className="btn-white">Book a free call →</Link>
                <a href="https://wa.me/919999999999" className="btn-outline-white" target="_blank" rel="noopener noreferrer">💬 WhatsApp us</a>
              </div>
            </div>
          </RevealDiv>
        </div>
      </section>

      {/* ── PORTFOLIO ── */}
      <section className="section" id="portfolio">
        <div className="container">
          <RevealDiv className="section-center" style={{ marginBottom: 48 }}>
            <div className="s-tag">Case Studies</div>
            <h2 className="s-head">Results, not promises</h2>
            <p className="s-sub">Real metrics from shipped products. No inflated numbers, no vanity stats.</p>
          </RevealDiv>
          <div className="port-grid">
            {portfolio.slice(0, 6).map(p => (
              <RevealDiv key={p.id}>
                <Link to={`/portfolio/${p.id}`} className="pcard">
                  <div className="pcard-thumb" style={{ background: p.thumbBg }}>{p.thumb}</div>
                  <div className="pcard-body">
                    <div className="pcard-cat">{p.category}</div>
                    <h3>{p.title}</h3>
                    <p>{p.summary}</p>
                    <div className="pcard-metrics">
                      {p.metrics.slice(0, 2).map(m => (
                        <div key={m.label} className="pm"><b>{m.value}</b> {m.label}</div>
                      ))}
                    </div>
                  </div>
                </Link>
              </RevealDiv>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/portfolio" className="btn-out">View all case studies →</Link>
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="section process-section">
        <div className="container">
          <RevealDiv className="section-center" style={{ marginBottom: 48 }}>
            <div className="s-tag">Process</div>
            <h2 className="s-head">From idea to impact</h2>
            <p className="s-sub">Five stages. Zero surprises. One outcome — a product that works.</p>
          </RevealDiv>
          <div className="process-grid">
            {processSteps.map(s => (
              <div key={s.num} className="proc-step">
                <div className="proc-n">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="section">
        <div className="container">
          <RevealDiv className="section-center" style={{ marginBottom: 48 }}>
            <div className="s-tag">Testimonials</div>
            <h2 className="s-head">Heard from real teams</h2>
            <p className="s-sub">Founders and CTOs who shipped with us — in their own words.</p>
          </RevealDiv>
          <div className="tgrid">
            {testimonials.map(t => (
              <RevealDiv key={t.id}>
                <div className="tcard">
                  <div className="tcard-stars">{'★'.repeat(t.stars)}</div>
                  <blockquote>{t.quote}</blockquote>
                  <div className="tcard-author">
                    <div className="tcard-av" style={{ background: t.bg }}>{t.avatar}</div>
                    <div>
                      <div className="tcard-name">{t.name}</div>
                      <div className="tcard-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="section final-cta-section">
        <div className="container">
          <RevealDiv className="section-center">
            <div className="s-tag">Let's build</div>
            <h2 className="s-head">Your next project starts here</h2>
            <p className="s-sub" style={{ margin: '0 auto 32px' }}>Tell us what you're working on. We respond within 24 hours with a plan and honest pricing.</p>
            <div className="hero-actions" style={{ justifyContent: 'center' }}>
              <Link to="/get-quote" className="btn-grad">Get a free quote →</Link>
              <Link to="/contact" className="btn-out">Contact us</Link>
            </div>
          </RevealDiv>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="sticky-cta">
        <a href="tel:+919999999999" className="sc-btn"><span className="icon">📞</span>Call</a>
        <a href="https://wa.me/919999999999" className="sc-btn" target="_blank" rel="noopener noreferrer"><span className="icon">💬</span>WhatsApp</a>
        <Link to="/get-quote" className="sc-btn primary"><span className="icon">✉️</span>Get Quote</Link>
      </div>
      <a href="https://wa.me/919999999999" className="wa-fab" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">💬</a>
    </div>
  );
}
